import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatEuro, API_BASE } from "@/lib/api";
import AppHeader from "@/components/AppHeader";
import { FileSpreadsheet, Banknote, Receipt, Wallet, Lock, Unlock, Trash2, FileText, Mail, ChevronLeft, Users, Ticket, Calculator } from "lucide-react";

export default function AbschlussPage() {
  const { showId } = useParams();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [txs, setTxs] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cashCheckOpen, setCashCheckOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, t, r] = await Promise.all([
        api.get(`/shows/${showId}/summary`),
        api.get(`/shows/${showId}/transactions`),
        api.get(`/shows/${showId}/reservations`),
      ]);
      setSummary(s.data);
      setTxs(t.data);
      setReservations(r.data);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showId]);

  const exportExcel = () => {
    window.open(`${API_BASE}/shows/${showId}/export.xlsx`, "_blank");
  };

  const toggleClose = async () => {
    if (summary?.show?.closed) {
      await api.post(`/shows/${showId}/reopen`);
    } else {
      if (!window.confirm("Vorstellung als abgeschlossen markieren?")) return;
      await api.post(`/shows/${showId}/close`);
    }
    load();
  };

  const deleteTx = async (id) => {
    if (!window.confirm("Transaktion stornieren?")) return;
    await api.delete(`/transactions/${id}`);
    load();
  };

  const toggleExtraRow = async () => {
    const current = summary?.show?.extra_row;
    if (current) {
      if (!window.confirm(`Zusatz-Reihe "${current.label}" entfernen?`)) return;
      await api.delete(`/shows/${showId}/extra-row`);
    } else {
      const label = window.prompt("Bezeichnung der Zusatz-Reihe (z.B. 3B):", "3B");
      if (!label) return;
      const countStr = window.prompt("Anzahl Sitze in der Zusatz-Reihe:", "14");
      const count = parseInt(countStr || "0", 10);
      if (!count || count < 1) return;
      await api.post(`/shows/${showId}/extra-row`, { label: label.trim(), count });
    }
    await load();
  };

  const emailInvoice = async (tx) => {
    const to = window.prompt("E-Mail-Adresse des Empfängers:", tx.invoice_email || "");
    if (!to) return;
    try {
      await api.post(`/transactions/${tx.id}/invoice/email`, { to });
      window.alert(`Rechnung ${tx.invoice_number} wurde an ${to} verschickt.`);
    } catch (e) {
      window.alert("Fehler beim Versand: " + (e.response?.data?.detail || e.message));
    }
  };

  const resMap = Object.fromEntries(reservations.map((r) => [r.id, r.name]));

  if (loading || !summary) return <div className="p-10 text-zinc-500">Lade…</div>;

  return (
    <div className="min-h-screen bg-zinc-50">
      <AppHeader
        title={`Tagesabschluss · ${summary.show.title}`}
        right={
          <>
            <button data-testid="abschluss-back" onClick={() => navigate(`/shows/${showId}/kassa`)}
              className="h-12 px-4 rounded-lg border-2 border-zinc-200 hover:bg-zinc-50 font-bold text-sm flex items-center gap-2">
              <ChevronLeft className="h-5 w-5" /> Zurück zur Kassa
            </button>
            <button data-testid="abschluss-cash-check" onClick={() => setCashCheckOpen(true)}
              className="h-12 px-4 rounded-lg border-2 border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-900 font-bold text-sm flex items-center gap-2">
              <Calculator className="h-4 w-4" /> Kassa prüfen
            </button>
            <button data-testid="abschluss-extra-row" onClick={toggleExtraRow}
              className={`h-12 px-4 rounded-lg border-2 font-bold text-sm flex items-center gap-2 ${
                summary?.show?.extra_row
                  ? "border-amber-400 bg-amber-100 hover:bg-amber-200 text-amber-900"
                  : "border-zinc-300 bg-white hover:bg-zinc-50 text-zinc-700"
              }`}>
              {summary?.show?.extra_row
                ? `Zusatz-Reihe ${summary.show.extra_row.label} entfernen`
                : "Zusatz-Reihe hinzufügen"}
            </button>
            <button data-testid="abschluss-toggle-close" onClick={toggleClose}
              className="h-12 px-4 rounded-lg border-2 border-zinc-200 hover:bg-zinc-50 font-bold text-sm flex items-center gap-2">
              {summary.show.closed ? <><Unlock className="h-4 w-4" /> Wieder öffnen</> : <><Lock className="h-4 w-4" /> Abschließen</>}
            </button>
            <button data-testid="abschluss-export-btn" onClick={exportExcel}
              className="h-12 px-4 rounded-lg bg-zinc-950 hover:bg-zinc-800 text-white font-bold flex items-center gap-2 text-sm">
              <FileSpreadsheet className="h-4 w-4" /> Excel exportieren
            </button>
          </>
        }
      />
      <main className="max-w-7xl mx-auto px-6 py-8 space-y-6">
        {/* Top metrics */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
          <Metric label="Anfangs-Wechselgeld" value={summary.start_cash} icon={Wallet} testid="metric-startcash" />
          <Metric label="Bareinnahmen" value={summary.cash_total} icon={Banknote} accent="emerald" testid="metric-cash" />
          <Metric label="Überweisungen" value={summary.invoice_total} icon={Receipt} accent="blue" testid="metric-invoice" />
          <Metric label="Gesamt-Einnahmen" value={summary.grand_total} icon={FileSpreadsheet} accent="dark" testid="metric-total" big />
          <VisitorMetric label="Besucher" count={summary.visitor_count || 0} testid="metric-visitors" />
          <VoucherMetric label="Gutscheine" count={summary.voucher_count || 0} testid="metric-vouchers-big" />
        </div>

        <div className="bg-white border border-zinc-200 rounded-xl p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">Kassenstand am Abend</p>
              <p className="text-xs text-zinc-500">Wechselgeld + Bareinnahmen</p>
              <p data-testid="metric-drawer" className="font-mono text-4xl font-black tabular-nums mt-2">
                {formatEuro(summary.cash_in_drawer)}
              </p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-zinc-500">Bar-Rechnungen</p>
              <p className="text-xs text-zinc-500">Vereine, sofort bar bezahlt</p>
              <p data-testid="metric-invoice-cash" className="font-mono text-2xl font-bold tabular-nums mt-2">
                {formatEuro(summary.invoice_cash_total || 0)}
              </p>
            </div>
          </div>
        </div>

        {/* Kassa-Prüfungs-Bestätigung */}
        {summary.cash_check && (
          <CashCheckConfirmation
            check={summary.cash_check}
            onReset={async () => {
              if (!window.confirm("Kassa-Prüfung wirklich entfernen, um neu zu zählen?")) return;
              await api.delete(`/shows/${showId}/cash-check`);
              await load();
            }}
          />
        )}

        {/* By category */}
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-zinc-100">
            <h3 className="text-xl font-black tracking-tight">Verkaufte Karten nach Kategorie</h3>
          </div>
          <table className="w-full">
            <thead className="bg-zinc-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs uppercase tracking-widest text-zinc-500">Kategorie</th>
                <th className="px-6 py-3 text-right text-xs uppercase tracking-widest text-zinc-500">Preis</th>
                <th className="px-6 py-3 text-right text-xs uppercase tracking-widest text-zinc-500">Anzahl</th>
                <th className="px-6 py-3 text-right text-xs uppercase tracking-widest text-zinc-500">Summe</th>
              </tr>
            </thead>
            <tbody>
              {simplifyCategories(summary.by_category).map((c) => (
                <tr key={c.key} data-testid={`cat-row-${c.key}`} className="border-b border-zinc-100">
                  <td className="px-6 py-3 font-semibold">{c.label}</td>
                  <td className="px-6 py-3 text-right font-mono tabular-nums">{formatEuro(c.price)}</td>
                  <td className="px-6 py-3 text-right font-mono tabular-nums">{c.qty}</td>
                  <td className="px-6 py-3 text-right font-mono font-bold tabular-nums">{formatEuro(c.revenue)}</td>
                </tr>
              ))}
              {(summary.voucher_discount_total || 0) > 0 && (
                <tr className="border-b border-purple-200 bg-purple-50">
                  <td className="px-6 py-3 font-semibold text-purple-900">Gutschein-Rabatt</td>
                  <td className="px-6 py-3 text-right text-purple-500">—</td>
                  <td data-testid="cat-row-gutschein-qty" className="px-6 py-3 text-right font-mono font-bold tabular-nums text-purple-900">{summary.voucher_count || 0}</td>
                  <td className="px-6 py-3 text-right font-mono font-bold tabular-nums text-purple-800">– {formatEuro(summary.voucher_discount_total)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Transactions */}
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between">
            <h3 className="text-xl font-black tracking-tight">Transaktionen ({txs.length})</h3>
          </div>
          {txs.length === 0 ? (
            <p className="px-6 py-8 text-zinc-500">Noch keine Transaktionen.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-widest text-zinc-500">Zeit</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-widest text-zinc-500">Typ</th>
                    <th className="px-4 py-3 text-left text-xs uppercase tracking-widest text-zinc-500">Empfänger / Reservierung</th>
                    <th className="px-4 py-3 text-right text-xs uppercase tracking-widest text-zinc-500">Summe</th>
                    <th className="px-4 py-3 text-right text-xs uppercase tracking-widest text-zinc-500">Gegeben</th>
                    <th className="px-4 py-3 text-right text-xs uppercase tracking-widest text-zinc-500">Rückgeld</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {txs.map((t) => (
                    <tr key={t.id} data-testid={`tx-row-${t.id}`} className="border-b border-zinc-100">
                      <td className="px-4 py-3 font-mono text-xs">{t.created_at.slice(11, 19)}</td>
                      <td className="px-4 py-3">
                        {t.payment_type === "cash" ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest bg-emerald-50 text-emerald-700 border border-emerald-200 px-2 py-1 rounded">
                            <Banknote className="h-3 w-3" /> Bar
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest bg-blue-50 text-blue-700 border border-blue-200 px-2 py-1 rounded">
                            <Receipt className="h-3 w-3" /> {t.invoice_number}
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {t.payment_type === "invoice"
                          ? t.invoice_club_name
                          : (t.reservation_id ? resMap[t.reservation_id] || "—" : "Freier Verkauf")}
                      </td>
                      <td className="px-4 py-3 text-right font-mono font-bold tabular-nums">{formatEuro(t.total)}</td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums">{t.given != null ? formatEuro(t.given) : "—"}</td>
                      <td className="px-4 py-3 text-right font-mono tabular-nums">{t.change != null ? formatEuro(t.change) : "—"}</td>
                      <td className="px-4 py-3">
                        <div className="flex gap-1.5 justify-end">
                          {t.payment_type === "invoice" && (
                            <>
                              <a
                                data-testid={`tx-invoice-${t.id}`}
                                href={`${API_BASE}/transactions/${t.id}/invoice.pdf`}
                                target="_blank" rel="noreferrer"
                                className="h-9 w-9 rounded-md border border-zinc-200 hover:bg-zinc-50 flex items-center justify-center"
                                title="Rechnung-PDF"
                              >
                                <FileText className="h-4 w-4" />
                              </a>
                              <button
                                data-testid={`tx-email-${t.id}`}
                                onClick={() => emailInvoice(t)}
                                className={`h-9 w-9 rounded-md border flex items-center justify-center ${t.email_sent_at ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-zinc-200 hover:bg-zinc-50"}`}
                                title={t.email_sent_at ? `Bereits versendet an ${t.email_sent_to}` : "Per E-Mail senden"}
                              >
                                <Mail className="h-4 w-4" />
                              </button>
                            </>
                          )}
                          <button data-testid={`tx-delete-${t.id}`} onClick={() => deleteTx(t.id)}
                            className="h-9 w-9 rounded-md border border-red-200 text-red-700 hover:bg-red-50 flex items-center justify-center"
                            title="Stornieren">
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      <CashCheckDialog
        open={cashCheckOpen}
        onClose={() => setCashCheckOpen(false)}
        expected={summary.cash_in_drawer || 0}
        showId={showId}
        onSaved={async () => { await load(); setCashCheckOpen(false); }}
      />
    </div>
  );
}

// ============================================================
// CashCheckConfirmation — Bestätigungs-Card im Tagesabschluss
// ============================================================
function CashCheckConfirmation({ check, onReset }) {
  const dt = check.checked_at ? new Date(check.checked_at) : null;
  const dtStr = dt ? dt.toLocaleString("de-AT", { dateStyle: "short", timeStyle: "short" }) : "";
  const sigUrl = `${API_BASE}/assets/signature_stangl.png`;
  const okStyle = check.is_ok
    ? "bg-emerald-50 border-emerald-300"
    : (check.diff > 0 ? "bg-amber-50 border-amber-300" : "bg-red-50 border-red-300");
  const okHeader = check.is_ok
    ? <p className="text-xl font-black text-emerald-900">✓ Kassaendstand geprüft und richtig</p>
    : <p className={`text-xl font-black ${check.diff > 0 ? "text-amber-900" : "text-red-900"}`}>
        Kassaendstand geprüft — {check.diff > 0 ? "Überschuss" : "Fehlbetrag"} {formatEuro(Math.abs(check.diff))}
      </p>;

  return (
    <div data-testid="cash-check-confirmation" className={`border-2 rounded-xl p-6 ${okStyle}`}>
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          {okHeader}
          <div className="grid grid-cols-2 gap-x-8 gap-y-1 mt-3 text-sm">
            <div>
              <span className="text-zinc-500">Soll:</span>{" "}
              <span className="font-mono font-bold tabular-nums">{formatEuro(check.expected_total)}</span>
            </div>
            <div>
              <span className="text-zinc-500">Gezählt:</span>{" "}
              <span className="font-mono font-bold tabular-nums">{formatEuro(check.counted_total)}</span>
            </div>
            <div>
              <span className="text-zinc-500">Geprüft am:</span>{" "}
              <span className="font-bold">{dtStr}</span>
            </div>
            <div>
              <span className="text-zinc-500">Geprüft durch:</span>{" "}
              <span className="font-bold">{check.signed_by}</span>
            </div>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <img src={sigUrl} alt="Unterschrift" className="h-16 w-auto" data-testid="cash-check-signature" />
          <p className="text-xs text-zinc-500 italic">Unterschrift Obmann</p>
          <button onClick={onReset} data-testid="cash-check-undo"
            className="mt-2 h-9 px-3 rounded-lg border border-zinc-300 hover:bg-white text-xs font-bold flex items-center gap-1.5">
            <Trash2 className="h-3 w-3" /> Prüfung zurücksetzen
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Cash-Check Dialog — Kassen-Stückelung eingeben und mit Soll abgleichen
// ============================================================
const DENOMINATIONS = [
  { label: "500 €", value: 500, kind: "Schein" },
  { label: "200 €", value: 200, kind: "Schein" },
  { label: "100 €", value: 100, kind: "Schein" },
  { label: "50 €", value: 50, kind: "Schein" },
  { label: "20 €", value: 20, kind: "Schein" },
  { label: "10 €", value: 10, kind: "Schein" },
  { label: "5 €", value: 5, kind: "Schein" },
  { label: "2 €", value: 2, kind: "Münze" },
  { label: "1 €", value: 1, kind: "Münze" },
  { label: "0,50 €", value: 0.5, kind: "Münze" },
  { label: "0,20 €", value: 0.2, kind: "Münze" },
  { label: "0,10 €", value: 0.1, kind: "Münze" },
  { label: "0,05 €", value: 0.05, kind: "Münze" },
  { label: "0,02 €", value: 0.02, kind: "Münze" },
  { label: "0,01 €", value: 0.01, kind: "Münze" },
];

function CashCheckDialog({ open, onClose, expected, showId, onSaved }) {
  const [counts, setCounts] = useState({});
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState("");

  useEffect(() => {
    if (open) { setCounts({}); setSaveErr(""); }
  }, [open]);

  if (!open) return null;

  const total = DENOMINATIONS.reduce((sum, d) => sum + (parseInt(counts[d.value] || 0, 10) * d.value), 0);
  const totalRounded = Math.round(total * 100) / 100;
  const diff = Math.round((totalRounded - expected) * 100) / 100;
  const isOk = Math.abs(diff) < 0.005;
  const hasAnyInput = Object.values(counts).some((v) => parseInt(v || 0, 10) > 0);

  const setQty = (val, qty) => {
    setCounts((c) => ({ ...c, [val]: qty.replace(/[^0-9]/g, "") }));
  };

  const reset = () => { setCounts({}); setSaveErr(""); };

  const confirmAndSave = async () => {
    setSaving(true);
    setSaveErr("");
    try {
      const denominations = {};
      Object.entries(counts).forEach(([v, q]) => {
        const n = parseInt(q || 0, 10);
        if (n > 0) denominations[String(v)] = n;
      });
      await api.post(`/shows/${showId}/cash-check`, {
        denominations,
        counted_total: totalRounded,
        expected_total: expected,
        diff,
        signed_by: "Sebastian Stangl",
      });
      onSaved?.();
    } catch (e) {
      setSaveErr(e.response?.data?.detail || e.message);
    }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 bg-zinc-950/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}
           data-testid="cash-check-dialog">
        <div className="p-8 space-y-6">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-3xl font-black tracking-tight flex items-center gap-3">
                <Calculator className="h-7 w-7 text-emerald-700" />
                Kassa prüfen
              </h2>
              <p className="text-zinc-500 text-base mt-1">Anzahl je Stückelung eingeben — das System rechnet zusammen und vergleicht mit dem Soll.</p>
            </div>
            <button onClick={onClose} className="h-10 w-10 rounded-lg border-2 border-zinc-200 hover:bg-zinc-50 text-xl">×</button>
          </div>

          {/* Soll-Wert */}
          <div className="bg-zinc-50 border-2 border-zinc-200 rounded-xl p-4 flex items-center justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.25em] font-bold text-zinc-500">Soll-Kassenstand</p>
              <p className="text-sm text-zinc-500">Wechselgeld + Bareinnahmen</p>
            </div>
            <p className="font-mono text-3xl font-black tabular-nums" data-testid="cash-check-expected">{formatEuro(expected)}</p>
          </div>

          {/* Stückelung — Eingabe-Tabelle */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
            {DENOMINATIONS.map((d) => {
              const qty = parseInt(counts[d.value] || 0, 10);
              const sub = qty * d.value;
              return (
                <div key={d.value} className="flex items-center gap-3 py-2 border-b border-zinc-100">
                  <span className={`w-20 text-base font-black ${d.kind === "Schein" ? "text-zinc-900" : "text-zinc-500"}`}>{d.label}</span>
                  <span className="text-xs text-zinc-400 w-12">{d.kind}</span>
                  <span className="text-sm text-zinc-500">×</span>
                  <input
                    data-testid={`cash-check-qty-${d.value}`}
                    type="number" inputMode="numeric" min="0"
                    value={counts[d.value] || ""}
                    onChange={(e) => setQty(d.value, e.target.value)}
                    placeholder="0"
                    className="w-20 h-11 px-2 text-center text-lg font-mono font-bold border-2 border-zinc-300 rounded-lg focus:outline-none focus:border-emerald-600"
                  />
                  <span className="flex-1 text-right font-mono text-sm tabular-nums text-zinc-700">
                    {sub > 0 ? formatEuro(sub) : "—"}
                  </span>
                </div>
              );
            })}
          </div>

          {/* Summe & Vergleich */}
          <div className="bg-zinc-50 border-2 border-zinc-200 rounded-xl p-5 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm uppercase tracking-[0.25em] font-bold text-zinc-500">Gezählter Bestand</span>
              <span className="font-mono text-3xl font-black tabular-nums" data-testid="cash-check-counted">{formatEuro(totalRounded)}</span>
            </div>
            <div className="flex items-center justify-between border-t border-zinc-200 pt-3">
              <span className="text-sm uppercase tracking-[0.25em] font-bold text-zinc-500">Differenz</span>
              <span data-testid="cash-check-diff" className={`font-mono text-2xl font-black tabular-nums ${isOk ? "text-emerald-700" : (diff > 0 ? "text-amber-700" : "text-red-700")}`}>
                {diff > 0 ? "+" : ""}{formatEuro(diff)}
              </span>
            </div>
          </div>

          {/* Ergebnis */}
          {hasAnyInput && (
            isOk ? (
              <div data-testid="cash-check-result-ok" className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-5 text-center">
                <p className="text-2xl font-black text-emerald-900">✓ Kassa stimmt</p>
                <p className="text-sm text-emerald-700 mt-1">Soll und Ist sind identisch.</p>
              </div>
            ) : (
              <div data-testid="cash-check-result-mismatch" className={`border-2 rounded-xl p-5 text-center ${diff > 0 ? "bg-amber-50 border-amber-300" : "bg-red-50 border-red-300"}`}>
                <p className={`text-2xl font-black ${diff > 0 ? "text-amber-900" : "text-red-900"}`}>
                  {diff > 0 ? "Überschuss" : "Fehlbetrag"}: {formatEuro(Math.abs(diff))}
                </p>
                <p className={`text-sm mt-1 ${diff > 0 ? "text-amber-700" : "text-red-700"}`}>
                  {diff > 0 ? "Es ist mehr Bargeld in der Kassa als erwartet." : "Es fehlt Bargeld in der Kassa."}
                </p>
              </div>
            )
          )}

          {/* Buttons */}
          {saveErr && <div data-testid="cash-check-save-err" className="bg-red-50 border-2 border-red-300 rounded-xl p-4 text-red-800 font-bold text-base">{saveErr}</div>}
          <div className="flex gap-3 justify-end pt-2">
            <button onClick={reset} data-testid="cash-check-reset"
              className="h-12 px-5 rounded-xl border-2 border-zinc-200 hover:bg-zinc-50 font-bold">
              Neu zählen
            </button>
            <button onClick={onClose} data-testid="cash-check-close"
              className="h-12 px-5 rounded-xl border-2 border-zinc-200 hover:bg-zinc-50 font-bold">
              Schließen
            </button>
            <button onClick={confirmAndSave} disabled={saving || !hasAnyInput} data-testid="cash-check-confirm"
              className="h-12 px-6 rounded-xl bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white font-black">
              {saving ? "Speichern…" : "Bestätigen & Speichern"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, icon: Icon, accent = "default", testid, big }) {
  const styles = {
    default: "bg-white border-zinc-200",
    emerald: "bg-emerald-50 border-emerald-200",
    blue: "bg-blue-50 border-blue-200",
    dark: "bg-zinc-950 border-zinc-950 text-white",
  };
  const labelStyle = accent === "dark" ? "text-zinc-300" : "text-zinc-500";
  return (
    <div className={`rounded-xl border p-5 ${styles[accent]}`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`h-4 w-4 ${accent === "dark" ? "text-zinc-300" : "text-zinc-500"}`} />
        <p className={`text-[10px] uppercase tracking-[0.25em] font-bold ${labelStyle}`}>{label}</p>
      </div>
      <p data-testid={testid} className={`font-mono ${big ? "text-4xl" : "text-3xl"} font-black tabular-nums tracking-tighter`}>
        {formatEuro(value)}
      </p>
    </div>
  );
}

function VisitorMetric({ label, count, testid }) {
  return (
    <div className="rounded-xl border bg-amber-50 border-amber-200 p-5">
      <div className="flex items-center gap-2 mb-2">
        <Users className="h-4 w-4 text-amber-700" />
        <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-amber-900">{label}</p>
      </div>
      <p data-testid={testid} className="font-mono text-4xl font-black tabular-nums tracking-tighter text-amber-900">
        {count}
      </p>
      <p className="text-[10px] text-amber-700 mt-1 font-bold">Personen im Saal</p>
    </div>
  );
}

function VoucherMetric({ label, count, testid }) {
  return (
    <div className="rounded-xl border bg-purple-50 border-purple-200 p-5">
      <div className="flex items-center gap-2 mb-2">
        <Ticket className="h-4 w-4 text-purple-700" />
        <p className="text-[10px] uppercase tracking-[0.25em] font-bold text-purple-900">{label}</p>
      </div>
      <p data-testid={testid} className="font-mono text-4xl font-black tabular-nums tracking-tighter text-purple-900">
        {count}
      </p>
      <p className="text-[10px] text-purple-700 mt-1 font-bold">eingelöst (= Freikarten)</p>
    </div>
  );
}

// Group _res and _no_res into one row per ticket type for the simplified UI.
function simplifyCategories(byCat) {
  const groups = {
    kinder: { key: "kinder", label: "Kind", price: 7, qty: 0, revenue: 0, order: 1 },
    erwachsene: { key: "erwachsene", label: "Erwachsen", price: 15, qty: 0, revenue: 0, order: 2 },
    sav: { key: "sav", label: "SAV", price: 12, qty: 0, revenue: 0, order: 3 },
  };
  for (const c of byCat || []) {
    const k = c.key.startsWith("kinder") ? "kinder"
      : c.key.startsWith("erwachsene") ? "erwachsene"
      : c.key.startsWith("sav") ? "sav" : null;
    if (!k) continue;
    groups[k].qty += c.qty || 0;
    groups[k].revenue += c.revenue || 0;
  }
  return Object.values(groups).sort((a, b) => a.order - b.order).filter((g) => g.qty > 0);
}
